import React from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import {
  Form,
  Select,
  SelectItem,
  Button,
  NumberInput,
  Checkbox,
} from "@heroui/react";
import { Bin, StorageRule } from "/app/src/models";

interface FormValues {
  binId?: string;
  minStockPerBin?: number;
  maxStockPerBin?: number;
  requiredCapacity?: number;
  isDefaultBin?: number | boolean;
}

/**
 * Renders a form to create a new storage rule for available bins.
 * @param {Object} params - The parameters for NewStorageRule.
 * @param {Bin[]} params.bins - List of all bins.
 * @param {(values: FormValues) => Promise<void> | void} params.onCreate - Callback invoked with form values when creating a rule.
 * @param {StorageRule[]} params.storageRules - Existing storage rules to determine available bins.
 * @returns {JSX.Element} The form component for adding a new storage rule.
 */
export default function NewStorageRule({
  bins,
  onCreate,
  storageRules,
}: {
  bins: Bin[];
  onCreate: (values: FormValues) => Promise<void> | void;
  storageRules: StorageRule[];
}) {
  const { t } = useTranslation();

  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: {
      binId: "",
      minStockPerBin: undefined,
      maxStockPerBin: undefined,
      requiredCapacity: undefined,
      isDefaultBin: false,
    },
  });

  const usedBinIds = new Set(storageRules.map((rule) => rule.binId));
  const availableBins = bins.filter((bin) => !usedBinIds.has(bin.id));

  return (
    <Form
      onSubmit={handleSubmit(async (values) => {
        await onCreate(values);
        reset();
      })}
      className="w-full"
    >
      <GridWrapper
        className="w-full items-start gap-6"
        responsive={{ sm: 1, md: 3, lg: 6 }}
      >
        <Controller
          control={control}
          name="binId"
          rules={{ required: true }}
          render={({ field, fieldState: { error } }) => (
            <div className="flex flex-col">
              <Select
                size="md"
                label={t("translation:bin")}
                placeholder={t("translation:select_bin")}
                labelPlacement="outside"
                selectedKeys={field.value ? [field.value] : []}
                onSelectionChange={(keys) => {
                  const val = Array.from(keys)[0];
                  field.onChange(val ? String(val) : undefined);
                }}
                isInvalid={Boolean(error)}
              >
                {availableBins.map((bin) => (
                  <SelectItem key={bin.id}>{bin.name}</SelectItem>
                ))}
              </Select>
              <div className="h-5 mt-1">
                {error && (
                  <p className="text-danger text-sm">
                    {t("translation:required")}
                  </p>
                )}
              </div>
            </div>
          )}
        />

        <Controller
          control={control}
          name="minStockPerBin"
          rules={{
            required: true,
            min: {
              value: 1,
              message: t("translation:min_stock_per_bin_validation"),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div className="flex flex-col">
              <NumberInput
                size="md"
                label={t("translation:min_stock_level")}
                labelPlacement="outside"
                value={field?.value}
                onValueChange={(value) => field.onChange(value ?? null)}
                isInvalid={Boolean(error)}
                min={1}
              />
              <div className="h-5 mt-1">
                {error && (
                  <p className="text-danger text-sm">
                    {error.message || t("translation:required")}
                  </p>
                )}
              </div>
            </div>
          )}
        />

        <Controller
          control={control}
          name="maxStockPerBin"
          rules={{ required: true }}
          render={({ field, fieldState: { error } }) => (
            <div className="flex flex-col">
              <NumberInput
                size="md"
                label={t("translation:max_stock_level")}
                labelPlacement="outside"
                value={field?.value}
                onValueChange={(value) => field.onChange(value ?? null)}
                isInvalid={Boolean(error)}
              />
              <div className="h-5 mt-1">
                {error && (
                  <p className="text-danger text-sm">
                    {t("translation:required")}
                  </p>
                )}
              </div>
            </div>
          )}
        />

        <Controller
          control={control}
          name="requiredCapacity"
          rules={{
            required: true,
            min: { value: 1, message: t("translation:required_capacity_min") },
            max: {
              value: 100,
              message: t("translation:required_capacity_max"),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div className="flex flex-col">
              <NumberInput
                size="md"
                label={t("translation:required_capacity")}
                labelPlacement="outside"
                value={field?.value}
                onValueChange={(value) => field.onChange(value ?? null)}
                isInvalid={Boolean(error)}
                min={1}
                max={100}
              />
              <div className="h-5 mt-1">
                {error && (
                  <p className="text-danger text-sm">
                    {error.message || t("translation:required")}
                  </p>
                )}
              </div>
            </div>
          )}
        />

        <Controller
          control={control}
          name="isDefaultBin"
          render={({ field }) => (
            <div className="flex flex-col">
              <label className="text-[16px] mb-4 -mt-[6px]">
                {t("translation:is_default_bin")}
              </label>
              <div>
                <Checkbox
                  isSelected={Boolean(field.value)}
                  onChange={field.onChange}
                />
              </div>
            </div>
          )}
        />

        <div className="flex items-center justify-end">
          <Button
            type="submit"
            color="primary"
            isLoading={formState.isSubmitting}
            className="min-w-32 mt-6"
          >
            {t("translation:add_rule")}
          </Button>
        </div>
      </GridWrapper>
    </Form>
  );
}
